import { useCreateConsumerNote } from "@features/cx/consumer-onboarding/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { toast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Label } from "@shared/ui/label";
import { Textarea } from "@shared/ui/textarea";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { MessageSquare, Send } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

interface NoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  itemId?: string;
  source: string;
  title?: string;
}

export function NoteDialog({
  isOpen,
  onClose,
  itemId,
  source,
  title,
}: NoteDialogProps) {
  const { consumer_id, consumerId, id } = useParams<{
    consumer_id?: string;
    consumerId?: string;
    id?: string;
  }>();

  const consumerIdFromUrl = consumer_id || consumerId || id;

  const createNoteMutation = useCreateConsumerNote();
  const fields: FormField[] = [
    {
      label: "Name",
      type: "text",
      name: "noteName",
      placeholder: "Enter Note Name"
    },
    {
      label: "Message",
      type: "textarea",
      name: "noteDescription",
      placeholder: "Enter Note Message"
    },
  ];

  const noteForm = FormService.createForm({ fields });
  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const handleSend = (values) => {
    createNoteMutation.mutateAsync(
      {
        consumer: consumerIdFromUrl,
        note_name: values?.noteName?.trim(),
        note_description: values?.noteDescription?.trim(),
        note_color: "bg-yellow-200",
      },
      {
        onSuccess: () => {
          handleClose();
          toast({
            title: "Note Added",
            description: "Note added successfully."
          })
        },
      }
    );
  };

  const customFooter = () => {
    return (
      <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" className="flex items-center gap-2">
              <Send className="h-4 w-4" />
              Add Note
            </Button>
          </div>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Add Note
          </DialogTitle>
          <DialogDescription>Add a note for selected {title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-gray-600">
              Note will be added to the {title}'s record
            </p>
          </div>

          <DynamicForm
            fields={fields}
            form={noteForm}
            config={{
              classes: FIELD_CLASSES,
              columns: 1,
              resetOnSubmit: true
            }}
            onSubmit={(values) => {handleSend(values)}}
            renderCustomFooter={customFooter}
          />

          
        </div>
      </DialogContent>
    </Dialog>
  );
}
